type Present<T> = Exclude<T, null | undefined | void>;

function isPresent<T>(value: T): value is Present<T> {
  return value != null;
}

/**
 * Return the first argument that is not null or undefined. The last argument
 * is a required default value, so the function always returns a value.
 *
 * This function functionally replaces `lodash.get` when combined with nullish
 * object accessors to access properties deep within objects while allowing a
 * default value.
 *
 * Code coverage will not detect if you fail to test your default. Use at your
 * own risk.
 *
 * If you need to be able to return null or undefined values, consider using the
 * `coalesce` function instead.
 *
 * @throws if no argument is present.
 *
 * @example
 * getWithDefault(obj.some?.nested?.value, defaultValue); // string
 *
 * @example BAD: lodash.get is not type safe!
 * lodash.get(obj, "some.nested.value", defaultValue); // any
 *
 * @example Multiple values
 * const a: string | undefined = undefined;
 * const b: string | undefined = undefined;
 * getWithDefault(a, b, "test"); // string
 *
 * @example Spread array
 * const numbers = [1, 2, 3];
 * getWithDefault(...numbers, 4);
 */
export function getWithDefault<T>(
  ...values: [...values: T[], defaultValue: Present<T>]
): Present<T> {
  for (const value of values) {
    if (isPresent(value)) {
      return value;
    }
  }

  throw new Error("Expected a default value, but none was present.");
}
